import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState('user');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await userService.getUser(id);
        setUser(res.data);
        setFormData({
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          email: res.data.email || '',
          phone_number: res.data.phone_number || '',
          city: res.data.city || '',
          address: res.data.address || '',
          street_number: res.data.street_number || '',
          fiscal_code: res.data.fiscal_code || ''
        });
      } catch (e) {
        setError('Errore nel caricamento dell\'utente');
        console.error(e);
      }
      setLoading(false);
    };
    loadUser();
  }, [id]);

  const canEdit = () => {
    if (!currentUser || !user) return false;
    if (currentUser.role === 'admin' || currentUser.role === 'superadmin') return true;
    // i moderatori possono modificare solo gli utenti
    return currentUser.role === 'moderator' && user.role === 'user';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!canEdit()) {
      setError('Non hai i permessi per modificare questo utente.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      await userService.updateUser(id, formData);
      setSuccessMessage('Dati utente aggiornati con successo.');
    } catch (e) {
      setError('Errore nel salvataggio dei dati');
      console.error(e);
    }
    setSaving(false);
  };

  const handleToggleActive = async () => {
    if (currentUser?.role === 'moderator' && user?.role === 'admin') {
      setError('Non puoi modificare lo stato di un amministratore.');
      return;
    }
    setActionInProgress(true);
    setError('');
    setSuccessMessage('');
    try {
      const payload = { is_active: !user.is_active };
      await userService.updateUser(id, payload);
      setUser(prev => ({ ...prev, is_active: !prev.is_active }));
      setSuccessMessage(`Utente ${!user.is_active ? 'attivato' : 'disattivato'} con successo.`);
    } catch (e) {
      setError('Errore nell\'aggiornamento dello stato.');
    }
    setActionInProgress(false);
  };

  const openRoleDialog = () => {
    setNewRole(user?.role || 'user');
    setDialogOpen(true);
  };

  const closeRoleDialog = () => setDialogOpen(false);

  const confirmRoleChange = async () => {
    if (!(currentUser?.role === 'admin' || currentUser?.role === 'superadmin' || (currentUser?.role === 'moderator' && user?.role === 'user'))) return;
    if (newRole === user.role) {
      closeRoleDialog();
      return;
    }
    setActionInProgress(true);
    setError('');
    setSuccessMessage('');
    try {
      await userService.updateUser(id, { role: newRole });
      setUser(prev => ({ ...prev, role: newRole }));
      setSuccessMessage('Ruolo aggiornato con successo.');
      closeRoleDialog();
    } catch (e) {
      setError('Errore nell\'aggiornamento del ruolo.');
    }
    setActionInProgress(false);
  };

  const canResetPassword = () => {
    if (!currentUser || !user) return false;
    return currentUser.role === 'admin' || currentUser.role === 'superadmin';
  };

  const handleResetPassword = async () => {
    if (!canResetPassword()) {
      setError('Non hai i permessi per resettare la password di questo utente.');
      return;
    }
    setResetLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await userService.resetPassword(id);
      setSuccessMessage('Password temporanea generata e inviata via email all\'utente.');
    } catch (e) {
      setError('Errore nel reset della password.');
    }
    setResetLoading(false);
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">Utente non trovato.</Alert>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/admin/users')}>Torna alla lista</Button>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="false">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography component="h1" variant="h4" gutterBottom align="center">
          Dettaglio Utente
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField label="Nome" name="first_name" value={formData.first_name} onChange={handleChange} fullWidth />
          <TextField label="Cognome" name="last_name" value={formData.last_name} onChange={handleChange} fullWidth />
          <TextField label="Email" name="email" value={formData.email} onChange={handleChange} fullWidth />
          <TextField label="Cellulare" name="phone_number" value={formData.phone_number} onChange={handleChange} fullWidth />
          <TextField label="Città" name="city" value={formData.city} onChange={handleChange} fullWidth />
          <TextField label="Indirizzo" name="address" value={formData.address} onChange={handleChange} fullWidth />
          <TextField label="Numero Civico" name="street_number" value={formData.street_number} onChange={handleChange} fullWidth />
          <TextField label="Codice Fiscale" name="fiscal_code" value={formData.fiscal_code} onChange={handleChange} fullWidth />
        </Box>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !canEdit()}>
            {saving ? 'Salvataggio...' : 'Salva modifiche'}
          </Button>
          {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin' || (currentUser?.role === 'moderator' && user.role === 'user')) && (
            <Button variant="contained" color="secondary" onClick={openRoleDialog} disabled={actionInProgress}>
              Cambia Ruolo
            </Button>
          )}
          {canResetPassword() && (
            <Button variant="outlined" color="warning" onClick={handleResetPassword} disabled={resetLoading}>
              {resetLoading ? 'Invio...' : 'Reset Password'}
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(`/admin/users/${id}/skills`)}
          >
            Gestisci Abilità
          </Button>
          <Button
            variant="contained"
            color={user.is_active ? 'error' : 'success'}
            onClick={handleToggleActive}
            disabled={actionInProgress || (currentUser?.role === 'moderator' && user.role === 'admin')}
          >
            {user.is_active ? 'Disattiva' : 'Attiva'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/admin/users')}>Torna alla lista</Button>
        </Box>
      </Paper>

      {/* Dialog cambio ruolo */}
      <Dialog open={dialogOpen} onClose={closeRoleDialog}>
        <DialogTitle>Cambia Ruolo Utente</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Seleziona il nuovo ruolo per l'utente {user?.email}:
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="role-select-label">Ruolo</InputLabel>
            <Select
              labelId="role-select-label"
              id="role-select"
              value={newRole}
              label="Ruolo"
              onChange={(e) => setNewRole(e.target.value)}
            >
              {currentUser?.role === 'superadmin' && <MenuItem value="superadmin">Super Amministratore</MenuItem>}
              {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                <MenuItem value="admin">Amministratore</MenuItem>
              )}
              {currentUser?.role === 'superadmin' && (
                <MenuItem value="moderator">Moderatore</MenuItem>
              )}
              {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin' || currentUser?.role === 'moderator') && (
                <MenuItem value="user">Utente</MenuItem>
              )}
              {currentUser?.role === 'moderator' && (
                <MenuItem value="moderator">Moderatore</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRoleDialog}>Annulla</Button>
          <Button onClick={confirmRoleChange} variant="contained" color="primary">Conferma</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserDetail;
